import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Interface for contact dropdown item
 */
export interface ContactDropdownItem {
  id: string;
  name: string;
  phone?: string;
  email?: string;
}

/**
 * Hook to fetch contacts for dropdown
 *
 * Returns all active contacts with minimal fields (id, name, phone, email)
 * Used in workflow node configurator and other forms
 *
 * @returns Query result with list of contacts
 *
 * @example
 * ```tsx
 * const { data: contacts, isLoading } = useContactsDropdown();
 *
 * <ContactsArrayInput
 *   contacts={contacts || []}
 *   values={values}
 *   onChange={onChange}
 * />
 * ```
 */
export const useContactsDropdown = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['contacts-dropdown'],
    queryFn: async () => {
      const response = await api.get('contacts/dropdown');

      // Backend returns { success: true, data: [...] }
      return response.data.data as ContactDropdownItem[];
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes - contacts change infrequently
    refetchOnWindowFocus: false,
  });
};
